
///
/// EarthBrowser
///
/// Created by Matt Giger on 7/12/2024
/// Copyright (c) 2024 Geodata Labs
///

import { Constants } from "@/constants"
import { Database } from "@/earthbrowser/database/Database"
import { DatabaseMigrations } from '@/earthbrowser/database/DatabaseMigrations'
import { AssetStatusTable } from "@/earthbrowser/database/tables/AssetStatusTable"
import { CacheTable } from "@/earthbrowser/database/tables/CacheTable"
import { GeoPackageCatalogTable } from "@/earthbrowser/database/tables/GeoPackageCatalogTable"
import { EarthBrowser } from "@/earthbrowser/EarthBrowser"
import { installContextLossGuard } from "@/earthbrowser/utils/ContextLossGuard"
import { initializeFileSystemAccess } from "@/earthbrowser/filesystem/FileSystemAccessManager"
import { useDatabaseLockCoordination } from "@/hooks/useDatabaseLockCoordination"
import { useWindowRestoration } from "@/hooks/useWindowRestoration"
import { markAppReady } from "@/utils/analytics"
import { cn } from "@/utils/utils"
import { useEffect, useRef, useState } from "react"
import { useDispatch } from "react-redux"
import { DatasetDataBrowser } from "./data-browsers/DatasetDataBrowser"
import { DatabaseLockStatusDialog } from "./dialogs/DatabaseLockStatusDialog"
import { AnchoredLocationForecastPanel } from "./controls/anchored-panel/AnchoredLocationForecastPanel"
import { AnchoredVolcanoInfoPanel } from "./controls/anchored-panel/AnchoredVolcanoInfoPanel"
import { AnchoredSatelliteInfoPanel } from "./controls/anchored-panel/AnchoredSatelliteInfoPanel"
import { AnchoredEarthquakeInfoPanel } from "./controls/anchored-panel/AnchoredEarthquakeInfoPanel"
import { QuakeCutoutHud } from "./controls/cutout/QuakeCutoutHud"
import { StormColumnHud } from "./controls/cutout/StormColumnHud"
import { AnchoredBuoyInfoPanel } from "./controls/anchored-panel/AnchoredBuoyInfoPanel"
import { AnchoredWebcamInfoPanel } from "./controls/anchored-panel/AnchoredWebcamInfoPanel"
import { AnchoredWildfireInfoPanel } from "./controls/anchored-panel/AnchoredWildfireInfoPanel"
import { AnchoredHurricaneInfoPanel } from "./controls/anchored-panel/AnchoredHurricaneInfoPanel"
import { AnchoredAlertInfoPanel } from "./controls/anchored-panel/AnchoredAlertInfoPanel"
import { AnchoredPlacemarkInfoPanel } from "./controls/anchored-panel/AnchoredPlacemarkInfoPanel"
import { DropPinMenu } from "./controls/DropPinMenu"
import { WebcamContactSheet } from "./features/webcam-browser/WebcamContactSheet"
import { LegendTray } from "./legends/LegendTray"
import { FileDropZone } from "./features/file-drop/FileDropZone"
import { DataDock } from "./windows/DataDock"
import { useViewportInsetsPublisher } from "@/hooks/useBottomChromeOffset"
// Module-level promise to track database cleanup during hot reload
let cleanupPromise: Promise<void> | null = null

/** Publishes the chrome insets to the engine; renders nothing */
function ViewportInsetsPublisher() {
	useViewportInsetsPublisher()
	return null
}

export function EarthBrowserCanvas({ className }: { className?: string }) {
	const dispatch = useDispatch()
	const canvasRef = useRef<HTMLCanvasElement>(null)
	const [database, setDatabase] = useState<Database | null>(null)
	const databaseRef = useRef<Database | null>(null)
	const { hasLock } = useDatabaseLockCoordination(database)

	// Restore persisted window states on launch
	useWindowRestoration()

	useEffect(() => {
		const initializeDatabase = async () => {
			// Wait for any previous database cleanup to complete (important for Vite hot reload)
			if (cleanupPromise) {
				await cleanupPromise
				cleanupPromise = null
			}

			const db = new Database(Constants.databaseName, Constants.volumeName)

			// Wait for database to be ready before initializing tables
			// The Database constructor queues the initialization asynchronously.
			// Calling db.version() will be queued AFTER init and wait for it to complete.
			await db.version()

			// Initialize tables
			void AssetStatusTable.create(db)
			CacheTable.initialize(db)
			GeoPackageCatalogTable.initialize(db)

			// Run database migrations before initializing tables
			await DatabaseMigrations.initialize(db)

			// NOW make the database available to React components
			// This ensures EarthBrowser won't start until tables are created
			Database.setDb(db)
			databaseRef.current = db
			setDatabase(db)

			// Clean up orphaned GeoPackage files and VFS volumes
			const { GeoPackageCleanup } = await import('@/earthbrowser/geopackage/GeoPackageCleanup')
			await GeoPackageCleanup.runStartupCleanup()

			// Initialize File System Access API manager
			void initializeFileSystemAccess()
		}

		void initializeDatabase()

		return () => {
			// Use ref to get the current database instance (not the stale closure value)
			Database.setDb(null)
			if (databaseRef.current) {
				// Track the cleanup promise so the next initialization can wait for it
				cleanupPromise = databaseRef.current.destroy()
				databaseRef.current = null
			}
		}
	}, [])

	useEffect(() => {
		// Wait for database to be fully initialized before starting EarthBrowser
		if(!canvasRef.current || !database) {
			return
		}

		const earthbrowser = new EarthBrowser(canvasRef.current)
		earthbrowser.run()
		// A lost WebGL context is a dead canvas: reload through crash recovery
		const uninstallContextLossGuard = installContextLossGuard(canvasRef.current)

		// Two frames after run() the engine has rendered at least once:
		// that is the app_ready / first_frame_ms mark for analytics
		requestAnimationFrame(() => requestAnimationFrame(() => markAppReady()))

		return () => {
			uninstallContextLossGuard()
			earthbrowser.destroy()
		}
	}, [dispatch, database])

	return (
		<>
			<FileDropZone className="fixed inset-0 w-full h-full">
				{/* select-none + no touch callout: a long press mid-gesture on
				    WKWebView must not pop the callout / magnifier */}
				<canvas
					ref={canvasRef}
					className={cn("touch-none select-none w-full h-full", className)}
					style={{ WebkitTouchCallout: "none" }}
					tabIndex={0}
					aria-label="Earth Browser 3D canvas"
				/>
			</FileDropZone>

			<DatabaseLockStatusDialog
				open={!hasLock}
				hasLock={hasLock}
			/>

			{/* Dataset File Browser */}
			<DatasetDataBrowser />

			<AnchoredLocationForecastPanel />
			<AnchoredVolcanoInfoPanel />
			<AnchoredSatelliteInfoPanel />
			<AnchoredEarthquakeInfoPanel />
			<QuakeCutoutHud />
			<StormColumnHud />
			<AnchoredBuoyInfoPanel />
			<AnchoredWebcamInfoPanel />
			<AnchoredWildfireInfoPanel />
			<AnchoredHurricaneInfoPanel />
			<AnchoredAlertInfoPanel />
			<AnchoredPlacemarkInfoPanel />

			{/* Long-press / right-click drop-pin context menu */}
			<DropPinMenu />
			{/* Webcam cluster contact sheet (long-press / right-click a cluster, or "Browse all" in a camera's panel) */}
			<WebcamContactSheet />

			<LegendTray />
			<ViewportInsetsPublisher />

			{/* Data dock: right-side rail + panel on desktop, bottom sheet on phones */}
			<DataDock />
		</>
	)
}